// Sugalarm — Cycle 474: the ONE owner of the foreground SETTLE window.
//
// WHY THIS EXISTS. Opening or returning to the phone app sometimes played
// several sounds at once (operator report, 2026-11-04). Cycles 112, 231,
// 235.3, 309, 351 and 429 each closed one path — a seeded alert, a backlog
// burst, an overdue cadence timer — and each time another emit path was
// still able to sound from pre-resume state: a cue the WebView held while
// hidden, the first tap replaying a tone that had already played, a timer
// that fell due during the freeze. The structural answer cycle 351 named:
// for a bounded moment after every return to the foreground NOTHING sounds,
// and when the moment ends the app sounds AT MOST ONE cue, chosen from the
// state the server has just confirmed.
//
// THE CONTRACT (read by `alarmCue.decideCue`, the one emit decision):
//   * `settling` begins on install (cold start) and, on a PHONE surface
//     (the "mobile" frame variant), on every return to the foreground: `visibilitychange` →
//     visible, `pageshow` with `persisted`, `online` after offline, the
//     Capacitor `appStateChange` → active the socket hook forwards, and a
//     heartbeat gap that proves the JS was frozen.
//   * While settling every cue request is DROPPED (never deferred).
//   * Settling ends when the active member's state is verified fresh from
//     the server (`markVerifiedFresh`: a snapshot on a socket opened after
//     the settle began, or a REST reseed that answered) or at the HARD CAP
//     `SETTLE_CAP_MS`, whichever is first. The cap is evaluated against the
//     wall clock at READ time, so a timer the WebView suspended can never
//     hold a real alarm past it.
//   * At the end, listeners run once (`onSettleEnd`) so the alarm hook can
//     evaluate the verified state and emit at most one cue.
//
// ⚠ THE KIOSK (AND THE DESKTOP BROWSER) SETTLE ON COLD START ONLY. A
// desktop tab is never suspended the way a phone WebView is, and the brief
// scopes resume settles to phone surfaces (regression-guard round 1). It is never backgrounded; when a
// cycle-473 Android Wi-Fi settings page covers it the document goes hidden
// and comes back, and a settle there would drop a cycle-374 urgent re-fire.
// So on the kiosk only the install-time settle exists (deploy reload, OTA,
// boot), bounded by the same cap, normally ended by the cycle-61 snapshot
// in under a second.
//
// In-memory only — nothing here touches storage, nothing survives a reload,
// a sign-out or a member switch.

/** The hard cap. A real alarm is never held longer than this on any
 *  surface (the safety reviewer's worst case). */
export const SETTLE_CAP_MS = 6_000;

/** A heartbeat tick that arrives this much later than scheduled means the
 *  JS was suspended (cycle 309's pattern). */
export const FREEZE_GAP_MS = 5_000;

/** One return to the foreground fires several signals — `visibilitychange`,
 *  then the shell's `appStateChange` and the first late heartbeat, up to a
 *  second or more apart (astra round 1, MEDIUM). A signal within this long of
 *  the last settle's START is the same resume: it never starts a second
 *  settle (whose end would replay the cue the first one sounded). */
export const RESUME_COALESCE_MS = 5_000;
const HEARTBEAT_MS = 1_000;

export type SettleReason =
  | "cold_start"
  | "visible"
  | "pageshow"
  | "online"
  | "app_active"
  | "freeze";

export type SettleEndedBy = "verified" | "cap";

export interface SettleEnd {
  reason: SettleReason;
  endedBy: SettleEndedBy;
  /** ms from the settle's start to its end. */
  durationMs: number;
  /** How long the document had been hidden before this settle (null on a
   *  cold start or when unknown). */
  hiddenMs: number | null;
  freeze: boolean;
}

interface LifecycleDeps {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => number;
  clearTimeout: (id: number) => void;
}

interface State {
  installed: boolean;
  /** A phone surface: resumes settle. Kiosk / desktop: cold start only. */
  phone: boolean;
  settling: boolean;
  startedAt: number;
  reason: SettleReason;
  /** Last time a settle began (the "resume" every replay ages against). */
  lastResumeAt: number;
  hiddenSince: number | null;
  hiddenMsAtStart: number | null;
  freezeAtStart: boolean;
  capTimer: number | null;
  wasOffline: boolean;
  /** A settle has started at least once (so `startedAt` is meaningful). */
  hasSettled: boolean;
  /** When the document last went hidden (null: never). */
  lastHiddenAt: number | null;
}

const defaultDeps = (): LifecycleDeps => ({
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clearTimeout: (id) => globalThis.clearTimeout(id),
});

let deps: LifecycleDeps = defaultDeps();
const fresh = (): State => ({
  installed: false,
  phone: false,
  settling: false,
  startedAt: 0,
  reason: "cold_start",
  lastResumeAt: Number.NEGATIVE_INFINITY,
  hiddenSince: null,
  hiddenMsAtStart: null,
  freezeAtStart: false,
  capTimer: null,
  wasOffline: false,
  hasSettled: false,
  lastHiddenAt: null,
});
let st: State = fresh();
const endListeners = new Set<(e: SettleEnd) => void>();
let uninstallFns: Array<() => void> = [];

function finish(endedBy: SettleEndedBy): void {
  if (!st.settling) return;
  const end: SettleEnd = {
    reason: st.reason,
    endedBy,
    durationMs: Math.max(0, deps.now() - st.startedAt),
    hiddenMs: st.hiddenMsAtStart,
    freeze: st.freezeAtStart,
  };
  st.settling = false;
  if (st.capTimer != null) {
    deps.clearTimeout(st.capTimer);
    st.capTimer = null;
  }
  for (const l of [...endListeners]) {
    try {
      l(end);
    } catch {
      /* a listener must never stop the others */
    }
  }
}

/** Begin a settle. Idempotent while one is running EXCEPT that a new
 *  resume re-anchors `lastResumeAt` (every replay ages against the newest
 *  resume). It never extends the running settle past its own cap. */
export function enterSettle(reason: SettleReason, opts: { freeze?: boolean } = {}): void {
  if (!st.installed) return;
  if (!st.phone && reason !== "cold_start") return;
  const now = deps.now();
  st.lastResumeAt = now;
  if (st.settling) return;
  // ⚠ Coalesce only DUPLICATE signals of one return: a document that went
  // hidden after the last settle began has been away again, and this is a
  // NEW return that must settle (astra round 2, HIGH — coalescing it left a
  // low that started while hidden silent).
  const awaySince = st.lastHiddenAt != null && st.lastHiddenAt >= st.startedAt;
  if (st.hasSettled && !awaySince && now - st.startedAt < RESUME_COALESCE_MS) return;
  st.hasSettled = true;
  st.settling = true;
  st.startedAt = now;
  st.reason = reason;
  st.hiddenMsAtStart = st.hiddenSince != null ? Math.max(0, now - st.hiddenSince) : null;
  st.freezeAtStart = opts.freeze === true;
  st.capTimer = deps.setTimeout(() => {
    st.capTimer = null;
    finish("cap");
  }, SETTLE_CAP_MS);
}

/** Is a settle running RIGHT NOW? Reads the cap against the wall clock, so
 *  a suspended cap timer can never stretch it. */
export function isSettling(): boolean {
  if (!st.settling) return false;
  if (deps.now() - st.startedAt >= SETTLE_CAP_MS) {
    finish("cap");
    return false;
  }
  return true;
}

/** The active member's state was just confirmed by the server. `sinceMs`
 *  is when the evidence was REQUESTED (the socket's connect time or the
 *  reseed's start): evidence older than the settle proves nothing about
 *  the present. */
export function markVerifiedFresh(sinceMs: number): void {
  if (!st.settling) return;
  if (sinceMs < st.startedAt) return;
  finish("verified");
}

/** When the newest settle began; -Infinity before any. */
export function lastResumeAt(): number {
  return st.lastResumeAt;
}

/** When the running settle began (null when none). */
export function settleStartedAt(): number | null {
  return isSettling() ? st.startedAt : null;
}

export function onSettleEnd(fn: (e: SettleEnd) => void): () => void {
  endListeners.add(fn);
  return () => endListeners.delete(fn);
}

export function isLifecycleInstalled(): boolean {
  return st.installed;
}

/** Install the window listeners and start the cold-start settle. Called
 *  once from the app shell. Returns the uninstaller (tests, HMR). */
export function installAppLifecycle(opts: { phone: boolean; deps?: Partial<LifecycleDeps> } = { phone: false }): () => void {
  if (st.installed) uninstallAppLifecycle();
  deps = { ...defaultDeps(), ...(opts.deps ?? {}) };
  st = fresh();
  st.installed = true;
  st.phone = opts.phone;
  enterSettle("cold_start");

  if (typeof window === "undefined" || typeof document === "undefined") {
    return uninstallAppLifecycle;
  }
  const onVis = () => {
    if (document.visibilityState === "hidden") {
      st.hiddenSince = deps.now();
      st.lastHiddenAt = st.hiddenSince;
      return;
    }
    enterSettle("visible");
    st.hiddenSince = null;
  };
  const onPageShow = (e: Event) => {
    if ((e as PageTransitionEvent).persisted) enterSettle("pageshow");
  };
  const onOffline = () => {
    st.wasOffline = true;
  };
  const onOnline = () => {
    if (!st.wasOffline) return;
    st.wasOffline = false;
    enterSettle("online");
  };
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);

  // The freeze detector: a 1 s heartbeat whose late arrival proves the JS
  // was suspended even when no visibility event fired (iOS lock screen).
  let lastBeat = deps.now();
  let beatTimer: number | null = null;
  const beat = () => {
    const now = deps.now();
    const gap = now - lastBeat;
    lastBeat = now;
    if (gap > HEARTBEAT_MS + FREEZE_GAP_MS && document.visibilityState !== "hidden") {
      enterSettle("freeze", { freeze: true });
    }
    beatTimer = deps.setTimeout(beat, HEARTBEAT_MS);
  };
  beatTimer = deps.setTimeout(beat, HEARTBEAT_MS);

  uninstallFns = [
    () => document.removeEventListener("visibilitychange", onVis),
    () => window.removeEventListener("pageshow", onPageShow),
    () => window.removeEventListener("offline", onOffline),
    () => window.removeEventListener("online", onOnline),
    () => {
      if (beatTimer != null) deps.clearTimeout(beatTimer);
    },
  ];
  return uninstallAppLifecycle;
}

/** The surface changed (a tablet rotated between the kiosk and phone
 *  layouts). Never starts or ends a settle by itself. */
export function setLifecyclePhone(phone: boolean): void {
  st.phone = phone;
}

/** Forwarded by the socket hook's Capacitor `appStateChange` listener (the
 *  shell's own resume signal, which iOS fires when `visibilitychange`
 *  does not). */
export function noteAppActive(): void {
  enterSettle("app_active");
}

export function uninstallAppLifecycle(): void {
  for (const f of uninstallFns.splice(0)) f();
  if (st.capTimer != null) deps.clearTimeout(st.capTimer);
  st = fresh();
  deps = defaultDeps();
}

/** Test seam: uninstall AND forget every listener. */
export function resetAppLifecycleForTests(): void {
  uninstallAppLifecycle();
  endListeners.clear();
}
